import {FC, useMemo} from 'react'
import {useLang} from './Metronici18n'
import {IntlProvider} from 'react-intl'
import '@formatjs/intl-relativetimeformat/polyfill'
import '@formatjs/intl-relativetimeformat/locale-data/en'
import '@formatjs/intl-relativetimeformat/locale-data/ar'

import enMessages from './messages/en.json'
import arMessages from './messages/ar.json'
import { WithChildren } from '../helpers'
import { tenantBrandName } from '../../app/tenant/tenantState'

const allMessages = {
  en: enMessages,
  ar: arMessages,
}

/**
 * The single brand-interpolation choke-point: rather than passing `{tenant_brand}` at
 * every `formatMessage` call site, the message VALUES carry the literal
 * `{tenant_brand}` placeholder and we swap it for the active tenant's name here, once,
 * before the messages reach `IntlProvider`. Deliberately NOT `{brand}`: that is a real
 * ICU argument (the PRODUCT's brand, e.g. `offers.from_barcode.brand_line`).
 * Only the `{tenant_brand}` substring is touched — real ICU args (`{name}`, …) survive.
 * Braces are dropped from the name: react-intl ICU-parses a message only when it's given values, so
 * quoting can't work on both paths, and a stray `{` would break parsing on the values path.
 */
export function applyBrandToMessages<T extends Record<string, string>>(
  messages: T,
  brandName: string
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key in messages) {
    const v = messages[key]
    out[key] =
      typeof v === 'string' && v.indexOf('{tenant_brand}') !== -1
        ? v.split('{tenant_brand}').join(brandName.replace(/[{}]/g, ''))
        : v
  }
  return out
}

const I18nProvider: FC<WithChildren> = ({children}) => {
  const locale = useLang()
  const messages = useMemo(
    () => applyBrandToMessages(allMessages[locale], tenantBrandName(locale)),
    [locale]
  )
  if (locale === 'ar') {
    document.querySelector('body')?.classList.add('lang-ar')
  } else {
    document.querySelector('body')?.classList.add('lang-en')

  }
  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  )
}

export {I18nProvider}
